import { ContinuousFeedbackDto } from "@coaching-culture/types";
import {
  Box,
  DatePill,
  Flex,
  Label,
  Loader,
  Modal,
  Text,
  sanitizeHtml,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { ScrollableContainer } from "components/ScrollableContainer";
import { UserFlag } from "components/UserFlag";
import ReactHtmlParser from "react-html-parser";
import { FaComment, FaPrayingHands } from "react-icons/fa";
import styled from "styled-components";

export type ContinuousFeedbackModalProps = {
  feedback: ContinuousFeedbackDto;
  onCancel: () => void;
  isOpen: boolean;
};

const DetailContent = styled.div`
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  padding: 12px;
  min-height: 100px;
`;

export const FeedbackTypeLabel = ({ type }: { type: string }) =>
  type === "praise" ? (
    <Label color="positive" icon={FaPrayingHands} size="small">
      Praise
    </Label>
  ) : (
    <Label color="feedback" icon={FaComment} size="small">
      Feedback
    </Label>
  );

export const ContinuousFeedbackModal = ({
  feedback,
  onCancel,
  isOpen,
}: ContinuousFeedbackModalProps) => {
  const [me] = useUser();

  if (!isOpen) {
    return null;
  }

  // The same modal shows feedback you were sent and feedback you sent, so
  // name the other person rather than always labelling them the sender.
  const isMine = feedback != null && feedback.sentBy.id === me.id;
  const person = isMine ? feedback.recievedBy : feedback?.sentBy;

  return (
    <Modal onClose={onCancel} width={600} showCloseButton>
      {feedback == null ? (
        <Loader />
      ) : (
        <Box p={4}>
          <Flex mb={3} flexDirection="column" alignItems="flex-start">
            <FeedbackTypeLabel type={feedback.type} />
            <Text
              as="h2"
              m={0}
              mt={2}
              fontWeight={600}
              fontSize="section"
              color="black"
            >
              {feedback.summary}
            </Text>
          </Flex>

          <Text fontWeight={600} fontSize="small" color="textMuted" mb={1}>
            {isMine ? "Sent to" : "Sender"}
          </Text>
          <UserFlag mb={3} user={person} />
          <Box mb={3}>
            <Text fontWeight={600} fontSize="small" color="textMuted" mb={1}>
              {isMine ? "Sent" : "Received"}
            </Text>
            <DatePill value={feedback.sentOn} />
          </Box>
          <Text fontWeight={600} fontSize="small" color="textMuted" mb={1}>
            Detail
          </Text>

          <DetailContent>
            <ScrollableContainer maxHeight={200}>
              {ReactHtmlParser(sanitizeHtml(feedback.detail))}
            </ScrollableContainer>
          </DetailContent>
        </Box>
      )}
    </Modal>
  );
};
